import { MapPin, Sprout, TreePine } from "lucide-react"

// Types for better type safety
export interface HomeSettings {
  brand: {
    name: string
    logo: {
      icon: any // Lucide icon component
      text: string
      source: string
      iconSrc?: string // Optional icon source for custom logos
    }
  }
  hero: {
    title: string
    subtitle: string
    description: string
    stats: {
      projects: string
      satisfaction: string
      support: string
    }
    ctaPrimary: string
    ctaSecondary: string
    form: {
      title: string
      fullName: string
      fullNamePlaceholder: string
      email: string
      emailPlaceholder: string
      company: string
      companyPlaceholder: string
      submit: string
    }
  }
  solutions: {
    title: string
    subtitle: string
    description: string
    items: Array<{
      icon: any
      iconSrc?: string
      title: string
      description: string
      features: string[]
    }>
  }
  services: {
    items: Array<{
      title: string
      description: string
      features: string[]
      icon: any
      iconSrc?: string
      imageAlt: string
      reverse: boolean
      buttonText: string
      imageSrc: string
    }>
  }
  pricing: {
    title: string
    subtitle: string
    description: string
    plans: Array<{
      name: string
      price: string
      period: string
      description: string
      features: string[]
      popular: boolean
    }>
  }
  testimonials: {
    title: string
    subtitle: string
    description: string
    items: Array<{
      name: string
      role: string
      company: string
      content: string
      avatar: string
      rating: number
    }>
  }
  stats: {
    title: string
    subtitle: string
    description: string
    items: Array<{
      value: string
      label: string
      description: string
    }>
  }
  cta: {
    title: string
    subtitle: string
    description: string
    primaryButton: string
    secondaryButton: string
  }
  contact: {
    title: string
    subtitle: string
    description: string
    form: {
      name: string
      email: string
      company: string
      message: string
      submit: string
    }
    info: {
      address: string
      phone: string
      email: string
    }
  }
}

// Default home page settings - easily configurable
export const defaultHomeSettings: HomeSettings = {
  brand: {
    name: "Dendromap",
    logo: {
      icon: TreePine,
      source: '/images/logo-name/white.svg',
      text: "Dendromap"
    }
  },
  hero: {
    title: "Transformez votre gestion",
    subtitle: "des arbres !",
    description: "Découvrez notre plateforme et notre application pour cartographier, inventorier et gérer efficacement vos arbres. Deux outils professionnels à porter d’un simple clic !",
    stats: {
      projects: "Projets réalisés",
      satisfaction: "Satisfaction client",
      support: "Support disponible"
    },
    ctaPrimary: "Commencer maintenant",
    ctaSecondary: "Voir la démo",
    form: {
      title: "Demandez un essai gratuit",
      fullName: "Nom complet",
      fullNamePlaceholder: "Votre nom complet",
      email: "Email",
      emailPlaceholder: "votre@email.com",
      company: "Entreprise",
      companyPlaceholder: "Nom de votre entreprise",
      submit: "Demander un essai gratuit"
    }
  },
  solutions: {
    title: "Nos",
    subtitle: "solutions",
    description: "Des outils innovants pour la gestion complète de vos espaces verts et inventaires d'arbres",
    items: [
      {
        icon: TreePine,
        iconSrc: "/images/logos/white.svg",
        title: "Inventaire d'arbres",
        description: "Cartographie précise et inventaire détaillé de tous vos arbres avec géolocalisation",
        features: ["Géolocalisation GPS", "Identification des espèces", "État sanitaire"]
      },
      {
        icon: MapPin,
        title: "Espaces verts urbains",
        description: "Gestion optimisée des espaces verts en milieu urbain pour les collectivités",
        features: ["Planification urbaine", "Maintenance préventive", "Rapports automatisés"]
      },
      {
        icon: Sprout,
        title: "Agriculture durable",
        description: "Solutions pour l'agriculture moderne et la gestion durable des cultures",
        features: ["Monitoring des cultures", "Optimisation des rendements", "Analyse prédictive"]
      }
    ]
  },
  services: {
    items: [
      {
        title: "Inventaire d'arbres professionnel",
        description: "Notre équipe d'experts réalise des inventaires complets avec les dernières technologies de cartographie et d'identification.",
        features: [
          "Identification précise des espèces",
          "Évaluation de l'état sanitaire",
          "Géolocalisation GPS haute précision",
          "Rapports détaillés et recommandations"
        ],
        icon: TreePine,
        iconSrc: "/images/logos/white.svg",
        imageAlt: "Expert en inventaire d'arbres",
        reverse: false,
        buttonText: "En savoir plus",
        imageSrc: "/images/Dendro-1.jpg"
      },
      {
        title: "Gestion des espaces verts urbains",
        description: "Optimisez la gestion de vos espaces verts urbains avec nos outils de planification et de maintenance intelligents.",
        features: [
          "Planification urbaine optimisée",
          "Maintenance préventive programmée",
          "Suivi en temps réel",
          "Tableaux de bord interactifs"
        ],
        icon: MapPin,
        imageAlt: "Espaces verts urbains",
        reverse: true,
        buttonText: "Découvrir",
        imageSrc: "/images/Dendro-2.jpg"
      },
      {
        title: "Agriculture durable et intelligente",
        description: "Révolutionnez vos pratiques agricoles avec nos solutions de monitoring et d'optimisation des cultures.",
        features: [
          "Monitoring en temps réel des cultures",
          "Optimisation des rendements",
          "Analyse prédictive avancée",
          "Gestion durable des ressources"
        ],
        icon: Sprout,
        imageAlt: "Agriculture durable",
        reverse: false,
        buttonText: "Explorer",
        imageSrc: "/images/Dendro-3.jpg"
      }
    ]
  },
  pricing: {
    title: "Tarifs",
    subtitle: "transparents",
    description: "Choisissez le plan qui correspond à vos besoins",
    plans: [
      {
        name: "Starter",
        price: "19.99€",
        period: "/mois",
        description: "Parfait pour débuter",
        features: ["Jusqu'à 100 arbres", "Cartographie basique", "Support email", "Rapports standards", "1 utilisateur"],
        popular: false
      },
      {
        name: "Professional",
        price: "49.99€",
        period: "/mois",
        description: "Pour les professionnels",
        features: [
          "Jusqu'à 1000 arbres",
          "Cartographie avancée",
          "Support prioritaire",
          "Rapports personnalisés",
          "5 utilisateurs",
          "API access"
        ],
        popular: true
      },
      {
        name: "Enterprise",
        price: "99€",
        period: "/mois",
        description: "Pour les grandes organisations",
        features: [
          "Arbres illimités",
          "Toutes les fonctionnalités",
          "Support 24/7",
          "Rapports avancés",
          "Utilisateurs illimités",
          "Intégrations personnalisées"
        ],
        popular: false
      },
      {
        name: "Custom",
        price: "Sur devis",
        period: "",
        description: "Solution sur mesure",
        features: [
          "Configuration personnalisée",
          "Développement spécifique",
          "Formation incluse",
          "Support dédié",
          "SLA garanti",
          "Déploiement on-premise"
        ],
        popular: false
      }
    ]
  },
  testimonials: {
    title: "Témoignages",
    subtitle: "clients",
    description: "Découvrez ce que nos clients disent de notre solution",
    items: [
      {
        name: "Marie Dubois",
        role: "Responsable Espaces Verts",
        company: "Ville de Lyon",
        content: "Dendromap a révolutionné notre gestion des espaces verts. L'interface est intuitive et les rapports sont d'une précision remarquable.",
        avatar: "/images/avatars/marie-dubois.jpg",
        rating: 5
      },
      {
        name: "Jean Martin",
        role: "Directeur",
        company: "Arboriculture Pro",
        content: "Grâce à Dendromap, nous avons pu optimiser nos inventaires et améliorer significativement notre productivité.",
        avatar: "/images/avatars/jean-martin.jpg",
        rating: 5
      },
      {
        name: "Sophie Leroy",
        role: "Chef de projet",
        company: "Green Solutions",
        content: "La cartographie GPS et l'identification automatique des espèces nous font gagner un temps précieux sur le terrain.",
        avatar: "/images/avatars/sophie-leroy.jpg",
        rating: 5
      }
    ]
  },
  stats: {
    title: "Nos",
    subtitle: "chiffres",
    description: "Des résultats qui parlent d'eux-mêmes",
    items: [
      {
        value: "500+",
        label: "Projets réalisés",
        description: "Plus de 500 projets d'inventaire et de cartographie"
      },
      {
        value: "98%",
        label: "Satisfaction client",
        description: "Taux de satisfaction exceptionnel"
      },
      {
        value: "24/7",
        label: "Support disponible",
        description: "Assistance technique en continu"
      },
      {
        value: "50+",
        label: "Espèces identifiées",
        description: "Base de données complète d'espèces"
      }
    ]
  },
  cta: {
    title: "Prêt à transformer",
    subtitle: "votre gestion d'arbres ?",
    description: "Rejoignez des centaines d'organisations qui font confiance à Dendromap pour leurs inventaires et cartographies.",
    primaryButton: "Commencer maintenant",
    secondaryButton: "Demander un essai gratuit"
  },
  contact: {
    title: "Contactez",
    subtitle: "notre équipe",
    description: "Notre équipe d'experts est là pour vous accompagner dans votre projet",
    form: {
      name: "Nom complet",
      email: "Email",
      company: "Entreprise",
      message: "Message",
      submit: "Envoyer le message"
    },
    info: {
      address: "123 Rue de la Forêt, 75001 Paris, France",
      phone: "+33 1 23 45 67 89",
      email: "contact@dendromap.fr"
    }
  }
}

// Hook to get home settings (can be extended to fetch from API/CMS in the future)
export const useHomeSettings = (): HomeSettings => {
  // For now, return default settings
  // In the future, this can be extended to:
  // 1. Fetch from a CMS like Strapi
  // 2. Load from environment variables
  // 3. Fetch from a database
  // 4. Use different settings based on locale/language

  return defaultHomeSettings
}

// Utility function to update settings (for future CMS integration)
export const updateHomeSettings = (newSettings: Partial<HomeSettings>): HomeSettings => {
  return {
    ...defaultHomeSettings,
    ...newSettings
  }
}
